/**
 * Proposals/AttachmentsStep — flag-ON Attachments wizard island (IP-15551 task-10).
 *
 * Mounted by react_component when proposal_deficiencies_tab_enabled? is true.
 * Wizard chrome (Back/Next) stays in the ERB form below this island.
 *
 * Invariant (enforced server-side): the attachments list never contains
 * any row originating from proposal_deficiency_photos.
 */
import React, { useRef, useState, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FileHandler } from '@ui/molecules/FileHandler';
import { Button } from '@ui';
import { SectionHeader } from '@ui/molecules/Header/SectionHeader';
import { Popup, PopupSection, PopupActionList } from '@ui/molecules/Popup';
import { translate } from '@utils/i18n';
import { useAttachmentsList, useRemoveAttachment, useUploadFile } from './hooks/useAttachmentsList';
import { useBuildingPhotos, useAttachBuildingPhoto } from './hooks/useBuildingPhotos';
import { BuildingPhotosDrawer } from './components/BuildingPhotosDrawer';
import { ReportsBrushaway } from './components/ReportsBrushaway';
import './AttachmentsStepStyles.scss';
const NS = 'features.proposals.attachments_step';
const t = (key, params = {}) => translate(key, Object.assign({ namespace: NS }, params));
function AttachmentsStepInner({ proposalId, attachmentsListUrl, buildingPhotosUrl, attachBuildingPhotoUrl, detachBuildingPhotoUrl, detachWorkOrderPhotoUrl, deleteProposalPhotoUrl, uploadAttachmentUrl, deleteAttachmentUrl, addReportUrl, removeReportUrl, inspectionReportsUrl, csrfToken, acceptTypes, maxFileSizeBytes }) {
    const addMenuAnchorRef = useRef(null);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [showBuildingPhotos, setShowBuildingPhotos] = useState(false);
    const [showReportsBrushaway, setShowReportsBrushaway] = useState(false);
    const [rejectError, setRejectError] = useState(null);
    // ── Data ────────────────────────────────────────────────────────────────
    const { data: items = [], isLoading: listLoading } = useAttachmentsList(proposalId, attachmentsListUrl);
    const { data: buildingPhotos = [], isLoading: photosLoading } = useBuildingPhotos(proposalId, buildingPhotosUrl, showBuildingPhotos);
    // ── Mutations ────────────────────────────────────────────────────────────
    const removeAttachment = useRemoveAttachment(proposalId, {
        deleteAttachmentUrl,
        deleteProposalPhotoUrl,
        detachBuildingPhotoUrl,
        detachWorkOrderPhotoUrl,
        removeReportUrl,
        proposalId
    });
    const uploadMutation = useUploadFile(proposalId, uploadAttachmentUrl, csrfToken);
    const attachPhotoMutation = useAttachBuildingPhoto(proposalId, attachBuildingPhotoUrl);
    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleFilesSelected = (files) => {
        files.forEach((file) => uploadMutation.mutate(file));
    };
    const handleReject = (files, reason) => {
        const names = files.map((f) => f.name).join(', ');
        setRejectError(reason === 'size'
            ? t('file_too_large', { max: Math.round(maxFileSizeBytes / 1024 / 1024), names })
            : t('unsupported_file_type', { names }));
    };
    const handleAddBuildingPhoto = (photo) => {
        attachPhotoMutation.mutate({ photoType: photo.photoType, photoId: photo.id });
    };
    // ── FileHandler items mapping ─────────────────────────────────────────────
    const fileHandlerItems = items.map((item) => {
        var _a;
        const ext = item.name.includes('.') ? (_a = item.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toUpperCase() : undefined;
        return {
            id: item.id,
            name: item.name,
            typeLabel: item.typeLabel,
            previewUrl: item.previewUrl,
            downloadUrl: item.downloadUrl,
            meta: ext
        };
    });
    // ── renderAction ──────────────────────────────────────────────────────────
    const renderAction = (fhItem) => {
        const source = items.find((i) => i.id === fhItem.id);
        if (!source)
            return null;
        const isUpload = source.sourceKind === 'upload';
        const label = isUpload ? `${t('delete')} ${source.name}` : `${t('remove')} ${source.name}`;
        return (React.createElement(Button, { variant: "default", onClick: () => removeAttachment.mutate(source), disabled: removeAttachment.isLoading, "aria-label": label },
            React.createElement("i", { className: "fa-light fa-circle-xmark", "aria-hidden": "true" })));
    };
    return (React.createElement("div", { className: "proposal-attachments-step" },
        rejectError && (React.createElement("div", { className: "alert alert-error proposal-attachments-step__error", role: "alert" },
            rejectError,
            React.createElement("button", { type: "button", onClick: () => setRejectError(null), "aria-label": t('dismiss') }, "\u00D7"))),
        React.createElement(SectionHeader, { title: t('section_title'), actions: React.createElement(Button, { ref: addMenuAnchorRef, isMenu: true, onClick: () => setShowAddMenu((v) => !v) },
                React.createElement("i", { className: "fa-light fa-circle-plus", "aria-hidden": "true" }),
                t('add_from_inspectpoint')) }),
        React.createElement(Popup, { anchor: addMenuAnchorRef.current, show: showAddMenu, onClose: () => setShowAddMenu(false), position: "bottom-left" },
            React.createElement(PopupSection, null,
                React.createElement(PopupActionList, null,
                    React.createElement(Button, { variant: "default", onClick: () => {
                            setShowAddMenu(false);
                            setShowReportsBrushaway(true);
                        } }, t('inspection_reports')),
                    React.createElement(Button, { variant: "default", onClick: () => {
                            setShowAddMenu(false);
                            setShowBuildingPhotos(true);
                        } }, t('building_photos'))))),
        React.createElement("div", { className: "proposal-attachments-step__body" }, listLoading ? (React.createElement("div", { className: "proposal-attachments-step__loading", "aria-live": "polite" },
            React.createElement("i", { className: "fa-light fa-spinner fa-spin", "aria-hidden": "true" }),
            " ",
            t('loading'))) : (React.createElement(FileHandler, { accept: acceptTypes, maxSize: maxFileSizeBytes, uploading: uploadMutation.isLoading, onFilesSelected: handleFilesSelected, onReject: handleReject, items: fileHandlerItems, renderAction: renderAction, dropzoneAriaLabel: t('file_handler_dropzone_aria'), listAriaLabel: t('file_handler_list_aria'), uploadingText: t('file_handler_uploading'), emptyText: t('file_handler_empty'), dropzoneHint: t('file_handler_dropzone_hint') }))),
        showBuildingPhotos && (React.createElement(BuildingPhotosDrawer, { open: showBuildingPhotos, photos: buildingPhotos, isLoading: photosLoading, onAdd: handleAddBuildingPhoto, onClose: () => setShowBuildingPhotos(false) })),
        React.createElement(ReportsBrushaway, { proposalId: proposalId, inspectionReportsUrl: inspectionReportsUrl, addReportUrl: addReportUrl, removeReportUrl: removeReportUrl, open: showReportsBrushaway, onClose: () => setShowReportsBrushaway(false) })));
}
export default function AttachmentsStep(props) {
    const queryClient = useMemo(() => new QueryClient({ defaultOptions: { queries: { staleTime: 30 * 1000, retry: 1 } } }), []);
    return (React.createElement(QueryClientProvider, { client: queryClient },
        React.createElement(AttachmentsStepInner, Object.assign({}, props))));
}
